import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { Head, router } from '@inertiajs/react'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  HiAdjustmentsHorizontal,
  HiMagnifyingGlass,
  HiOutlineBriefcase,
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineEye,
  HiXMark,
} from 'react-icons/hi2'
import Avatar from '../../components/Avatar'
import EventLayout from '../../components/EventLayout'
import FilterDrawer, {
  EMPTY_FILTERS,
  FilterFacet,
  FiltersState,
  filtersActiveCount,
} from '../../components/FilterDrawer'
import PriorityBadge from '../../components/PriorityBadge'
import SortByMenu, { SortKey, sortLeads } from '../../components/SortByMenu'

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

type EventStatus = 'upcoming' | 'ongoing' | 'completed'

type LeadPriority = 'hot' | 'warm' | 'cold'

type LeadStage = 'prospect' | 'in_progress' | 'proposal_sent'

type Lead = {
  id: number
  company: string
  name: string
  email: string
  priority: LeadPriority
  stage: LeadStage
  won: boolean
  assignee: { id: number; name: string } | null
  created_at_display: string
  updated_at_display: string
  option_ids: number[]
}

type AssignableUser = { id: number; name: string; role: 'manager' | 'sales' }

type Props = {
  event: {
    id: number
    title: string
    slug: string
    location: string
    date_range: string
    status: EventStatus
    banner_url: string | null
  }
  events: Array<{ title: string; slug: string }>
  active_tab: 'stages'
  leads: Lead[]
  filter_facets: FilterFacet[]
  assignable_users: AssignableUser[]
}

// ────────────────────────────────────────────────────────────────
// Display labels
// ────────────────────────────────────────────────────────────────

const STAGES: LeadStage[] = ['prospect', 'in_progress', 'proposal_sent']

const STAGE_LABELS: Record<LeadStage, string> = {
  prospect: 'Prospect',
  in_progress: 'In progress',
  proposal_sent: 'Proposal Sent',
}

// ────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────

export default function Stages({
  event,
  events,
  leads: initialLeads,
  filter_facets,
  assignable_users,
}: Props) {
  // Local mirror so drag-and-drop can update optimistically without
  // waiting for an Inertia round-trip.
  const [leads, setLeads] = useState<Lead[]>(initialLeads)
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState<SortKey>('default')
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS)
  const [filterOpen, setFilterOpen] = useState(false)
  const [activeMobileStage, setActiveMobileStage] = useState<LeadStage>('prospect')
  const [moveSheetLead, setMoveSheetLead] = useState<Lead | null>(null)
  const [draggingId, setDraggingId] = useState<number | null>(null)

  // Re-sync from server when the prop changes (event switch, navigation).
  useEffect(() => {
    setLeads(initialLeads)
  }, [initialLeads])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let base = q
      ? leads.filter(
          (l) =>
            l.name.toLowerCase().includes(q) ||
            l.company.toLowerCase().includes(q) ||
            l.email.toLowerCase().includes(q),
        )
      : leads

    // Apply drawer filters. Semantics match the server: OR within a
    // group, AND across groups.
    if (filters.priority.length > 0) {
      base = base.filter((l) => filters.priority.includes(l.priority))
    }
    if (filters.assignee_ids.length > 0) {
      base = base.filter((l) => l.assignee && filters.assignee_ids.includes(l.assignee.id))
    }
    for (const [stepId, optionIds] of Object.entries(filters.answers)) {
      if (optionIds.length === 0) continue
      // stepId is informational only — the lead's option_ids list is
      // flat across all steps, and option_ids are unique per question
      // via the EventStepOption → EventStep FK, so membership check is
      // sufficient.
      void stepId
      base = base.filter((l) => l.option_ids.some((id) => optionIds.includes(id)))
    }

    return sortLeads(base, sort)
  }, [leads, search, sort, filters])

  // Group + sort by created_at desc — server already orders, so we
  // preserve insertion order per stage.
  const grouped: Record<LeadStage, Lead[]> = useMemo(
    () => ({
      prospect: filtered.filter((l) => l.stage === 'prospect'),
      in_progress: filtered.filter((l) => l.stage === 'in_progress'),
      proposal_sent: filtered.filter((l) => l.stage === 'proposal_sent'),
    }),
    [filtered],
  )

  const moveLead = (leadId: number, nextStage: LeadStage) => {
    const prev = leads
    setLeads((ls) => ls.map((l) => (l.id === leadId ? { ...l, stage: nextStage } : l)))
    router.patch(
      `/leads/${leadId}/update_stage`,
      { stage: nextStage },
      {
        preserveScroll: true,
        preserveState: true,
        onError: () => setLeads(prev),
      },
    )
  }

  // 8px activation distance so taps/clicks on cards aren't hijacked.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  )

  const handleDragStart = (e: DragStartEvent) => {
    setDraggingId(Number(e.active.id))
  }

  const handleDragEnd = (e: DragEndEvent) => {
    setDraggingId(null)
    const { active, over } = e
    if (!over) return
    const leadId = Number(active.id)
    const nextStage = over.id as LeadStage
    const lead = leads.find((l) => l.id === leadId)
    if (!lead || lead.stage === nextStage) return
    moveLead(leadId, nextStage)
  }

  const draggingLead = draggingId ? (leads.find((l) => l.id === draggingId) ?? null) : null

  return (
    <>
      <Head title={`${event.title} · Stages`} />
      <EventLayout event={event} events={events} active="stages">
        <Toolbar
          search={search}
          sort={sort}
          activeFilterCount={filtersActiveCount(filters)}
          onSearch={setSearch}
          onSortChange={setSort}
          onFilterClick={() => setFilterOpen(true)}
        />

        {/* ── Mobile: segmented stage tabs ─────────────────────── */}
        <div className="mt-4 md:hidden">
          <MobileStageTabs
            active={activeMobileStage}
            counts={{
              prospect: grouped.prospect.length,
              in_progress: grouped.in_progress.length,
              proposal_sent: grouped.proposal_sent.length,
            }}
            onChange={setActiveMobileStage}
          />
        </div>

        {/* ── Desktop: 3-column kanban with DnD ────────────────── */}
        <div className="-mx-5 mt-4 hidden bg-[#E4E4E4] px-5 pb-16 pt-5 md:-mx-10 md:block md:px-10">
          <DndContext
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="grid grid-cols-3 gap-4">
              {STAGES.map((stage) => (
                <KanbanColumn
                  key={stage}
                  stage={stage}
                  leads={grouped[stage]}
                  draggingId={draggingId}
                />
              ))}
            </div>
            <DragOverlay>
              {draggingLead ? <LeadCard lead={draggingLead} dragging /> : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* ── Mobile: single column for active stage ──────────── */}
        <div className="-mx-5 mt-3 bg-[#E4E4E4] px-5 pb-16 pt-4 md:hidden">
          <MobileColumn
            stage={activeMobileStage}
            leads={grouped[activeMobileStage]}
            onCardTap={(lead) => setMoveSheetLead(lead)}
          />
        </div>
      </EventLayout>

      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
        value={filters}
        facets={filter_facets}
        assignees={assignable_users}
      />

      {moveSheetLead && (
        <MoveBottomSheet
          lead={moveSheetLead}
          onMove={(nextStage) => {
            moveLead(moveSheetLead.id, nextStage)
            setMoveSheetLead(null)
          }}
          onClose={() => setMoveSheetLead(null)}
        />
      )}
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// Toolbar — search field + filter button (filter is a stub)
// ────────────────────────────────────────────────────────────────

function Toolbar({
  search,
  sort,
  activeFilterCount,
  onSearch,
  onSortChange,
  onFilterClick,
}: {
  search: string
  sort: SortKey
  activeFilterCount: number
  onSearch: (q: string) => void
  onSortChange: (sort: SortKey) => void
  onFilterClick: () => void
}) {
  const [value, setValue] = useState(search)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    setValue(search)
  }, [search])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setValue(next)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => onSearch(next.trim()), 200)
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <label className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-midnight-green md:max-w-sm md:flex-none md:w-[400px]">
        <HiMagnifyingGlass className="h-4 w-4 text-gray-400" aria-hidden="true" />
        <input
          type="search"
          value={value}
          onChange={handleChange}
          placeholder="Search"
          className="w-full border-0 bg-transparent p-0 font-display text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
        />
      </label>
      <div className="flex items-center gap-2">
        <SortByMenu sort={sort} onChange={onSortChange} />
        <button
          type="button"
          onClick={onFilterClick}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 font-display text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <HiAdjustmentsHorizontal className="h-4 w-4" aria-hidden="true" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-midnight-green px-1.5 font-display text-[10px] font-semibold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// KanbanColumn — desktop column wrapped in useDroppable
// ────────────────────────────────────────────────────────────────

function KanbanColumn({
  stage,
  leads,
  draggingId,
}: {
  stage: LeadStage
  leads: Lead[]
  draggingId: number | null
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage })

  return (
    <div className="flex min-w-0 flex-col">
      <ColumnHeader stage={stage} count={leads.length} />
      <div
        ref={setNodeRef}
        className={`flex flex-col gap-2 rounded-xl px-2 pb-10 pt-2 transition-colors ${
          isOver ? 'bg-[#cfcfcf] ring-2 ring-midnight-green/40' : 'bg-[#D9D9D9]'
        }`}
      >
        {leads.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-400 px-3 py-6 text-center font-display text-xs text-gray-500">
            Drop leads here
          </div>
        ) : (
          leads.map((lead) => (
            <DraggableCard key={lead.id} lead={lead} hidden={lead.id === draggingId} />
          ))
        )}
      </div>
    </div>
  )
}

function ColumnHeader({ stage, count }: { stage: LeadStage; count: number }) {
  return (
    <div className="mb-2 flex items-center gap-3 rounded-t-xl bg-white px-4 py-4">
      <span className="font-display text-base font-medium text-ink">{STAGE_LABELS[stage]}</span>
      <span className="inline-flex h-6 min-w-[26px] items-center justify-center rounded-full border border-black/40 px-2 font-display text-xs font-medium text-black">
        {count}
      </span>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// DraggableCard — wraps LeadCard with @dnd-kit useDraggable
// ────────────────────────────────────────────────────────────────

function DraggableCard({ lead, hidden }: { lead: Lead; hidden: boolean }) {
  const { attributes, listeners, setNodeRef } = useDraggable({ id: lead.id })
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={{ opacity: hidden ? 0 : 1 }}
      className="cursor-grab touch-none active:cursor-grabbing"
    >
      <LeadCard lead={lead} />
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// LeadCard — presentational card matching Figma
// ────────────────────────────────────────────────────────────────

function LeadCard({ lead, dragging = false }: { lead: Lead; dragging?: boolean }) {
  return (
    <div
      className={`relative flex flex-col gap-5 rounded-xl bg-white p-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)] ${
        dragging ? 'rotate-2 ring-2 ring-midnight-green/40' : ''
      }`}
    >
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          router.visit(`/leads/${lead.id}`)
        }}
        className="absolute bottom-3 right-3 cursor-pointer text-gray-400 transition hover:text-midnight-green"
        aria-label={`View ${lead.name}`}
      >
        <HiOutlineEye className="h-5 w-5" />
      </button>
      <div className="flex flex-col gap-1.5">
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <p className="truncate font-display text-sm font-medium text-ink">{lead.name}</p>
            <p className="truncate font-display text-xs font-medium text-black/40">
              {lead.email}
            </p>
          </div>
          <Avatar id={lead.id} name={lead.name} size="lg" />
        </div>
        <div>
          <PriorityBadge priority={lead.priority} />
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <CardMetaRow icon={<HiOutlineBriefcase className="h-4 w-4" />} text={lead.company} />
        <CardMetaRow
          icon={<HiOutlineCalendarDays className="h-4 w-4" />}
          label="Created"
          text={lead.created_at_display}
        />
        <CardMetaRow
          icon={<HiOutlineClock className="h-4 w-4" />}
          label="Updated"
          text={lead.updated_at_display}
        />
      </div>
    </div>
  )
}

function CardMetaRow({
  icon,
  text,
  label,
}: {
  icon: React.ReactNode
  text: string
  label?: string
}) {
  return (
    <div className="flex items-center gap-2 font-display text-xs font-medium text-black/60">
      <span className="text-black/60" title={label}>
        {icon}
      </span>
      {label && <span>{label}:</span>}
      <span className="truncate">{text}</span>
    </div>
  )
}

// ────────────────────────────────────────────────────────────────
// Mobile: pill tabs + single column + bottom sheet for "Move to..."
// ────────────────────────────────────────────────────────────────

function MobileStageTabs({
  active,
  counts,
  onChange,
}: {
  active: LeadStage
  counts: Record<LeadStage, number>
  onChange: (stage: LeadStage) => void
}) {
  return (
    <div
      role="tablist"
      className="flex gap-1 rounded-lg border border-gray-300 bg-white p-1 shadow-sm"
    >
      {STAGES.map((stage) => {
        const isActive = stage === active
        return (
          <button
            key={stage}
            role="tab"
            type="button"
            onClick={() => onChange(stage)}
            className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-2 font-display text-xs font-medium transition ${
              isActive
                ? 'bg-midnight-green text-white shadow-sm'
                : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            <span>{STAGE_LABELS[stage]}</span>
            <span
              className={`inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] ${
                isActive ? 'bg-white/20 text-white' : 'border border-black/40 text-black'
              }`}
            >
              {counts[stage]}
            </span>
          </button>
        )
      })}
    </div>
  )
}

function MobileColumn({
  stage,
  leads,
  onCardTap,
}: {
  stage: LeadStage
  leads: Lead[]
  onCardTap: (lead: Lead) => void
}) {
  if (leads.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-400 bg-[#D9D9D9] px-3 py-12 text-center font-display text-sm text-gray-600">
        No leads in {STAGE_LABELS[stage]}
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2 rounded-xl bg-[#D9D9D9] p-2">
      {leads.map((lead) => (
        <div
          key={lead.id}
          role="button"
          tabIndex={0}
          onClick={() => onCardTap(lead)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              onCardTap(lead)
            }
          }}
          className="cursor-pointer text-left transition active:scale-[0.99]"
        >
          <LeadCard lead={lead} />
        </div>
      ))}
    </div>
  )
}

// Bottom sheet shown after tapping a card on mobile. Lists the *other*
// two stages so the user can move the lead. Trello/Linear pattern.
function MoveBottomSheet({
  lead,
  onMove,
  onClose,
}: {
  lead: Lead
  onMove: (stage: LeadStage) => void
  onClose: () => void
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const otherStages = STAGES.filter((s) => s !== lead.stage)

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center md:hidden">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />
      <div className="relative w-full max-w-md rounded-t-2xl bg-white p-5 shadow-xl">
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-gray-300" />
        <div className="mb-4 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate font-display text-base font-semibold text-ink">{lead.name}</p>
            <p className="truncate font-display text-xs text-black/50">
              Currently in {STAGE_LABELS[lead.stage]}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 transition hover:text-gray-700"
            aria-label="Close"
          >
            <HiXMark className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-2 font-display text-xs font-medium uppercase tracking-wide text-black/50">
          Move to
        </p>
        <div className="flex flex-col gap-2">
          {otherStages.map((stage) => (
            <button
              key={stage}
              type="button"
              onClick={() => onMove(stage)}
              className="flex items-center justify-between rounded-lg border border-gray-200 px-4 py-3 font-display text-sm font-medium text-ink transition hover:bg-gray-50 active:bg-gray-100"
            >
              <span>{STAGE_LABELS[stage]}</span>
              <span className="font-display text-xs text-black/40">→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
